import React, { useState, useEffect, useRef } from 'react';
import { allCommands } from '../data/cyberCommands';

// Scrolling vertical columns of real cybersecurity commands
const CommandStream = ({ columns = 4, speed = 30, opacity = 0.06, className = '' }) => {
  const containerRef = useRef(null);
  const [columnData, setColumnData] = useState([]);

  useEffect(() => {
    // Create columns with shuffled commands
    const cols = [];
    for (let i = 0; i < columns; i++) {
      const shuffled = [...allCommands].sort(() => Math.random() - 0.5);
      cols.push(shuffled);
    }
    setColumnData(cols);
  }, [columns]);

  if (columnData.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      style={{ opacity }}
    >
      <div className="flex h-full gap-0" style={{ width: '100%' }}>
        {columnData.map((commands, colIdx) => (
          <div
            key={colIdx}
            className="flex-1 overflow-hidden relative"
          >
            <div
              className="animate-scroll-up font-mono text-cyber-green whitespace-nowrap"
              style={{
                animation: `scrollUp ${speed + colIdx * 8}s linear infinite`,
                fontSize: '10px',
                lineHeight: '1.8',
                paddingTop: `${colIdx * 200}px`,
              }}
            >
              {/* Double the commands for seamless loop */}
              {[...commands, ...commands].map((cmd, i) => (
                <div key={i} className="truncate px-2" style={{ maxWidth: '100%' }}>
                  <span className="text-cyber-green/60">{'>'}</span> {cmd}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Horizontal scrolling command ticker
export const CommandTicker = ({ speed = 60, className = '' }) => {
  const commands = useRef(
    [...allCommands].sort(() => Math.random() - 0.5).slice(0, 80)
  ).current;

  return (
    <div className={`overflow-hidden whitespace-nowrap ${className}`}>
      <div
        className="inline-block"
        style={{
          animation: `scrollLeft ${speed}s linear infinite`,
        }}
      >
        {commands.map((cmd, i) => (
          <span key={i} className="text-xs font-mono text-cyber-green/20 mx-6">
            <span className="text-cyber-cyan/20">$</span> {cmd}
          </span>
        ))}
      </div>
      <div
        className="inline-block"
        style={{
          animation: `scrollLeft ${speed}s linear infinite`,
        }}
      >
        {commands.map((cmd, i) => (
          <span key={`dup-${i}`} className="text-xs font-mono text-cyber-green/20 mx-6">
            <span className="text-cyber-cyan/20">$</span> {cmd}
          </span>
        ))}
      </div>
    </div>
  );
};

const FEED_PREFIXES = ['[RECON]', '[SCAN]', '[EXPLOIT]', '[ENUM]', '[CRACK]', '[SNIFF]',
  '[FUZZ]', '[PIVOT]', '[EXFIL]', '[DETECT]', '[AUDIT]', '[HUNT]', '[PROBE]',
  '[TRACE]', '[DECRYPT]', '[INJECT]', '[DUMP]', '[PARSE]', '[RELAY]', '[INTERCEPT]'];

const FEED_STATUSES = ['OK', 'OK', 'OK', 'OK', 'DONE', 'PASS', 'WARN', 'ACTIVE'];

// Live-feed style command terminal (shows commands appearing one by one)
export const LiveCommandFeed = ({ count = 20, interval = 2000, className = '' }) => {
  const [lines, setLines] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const commands = useRef(
    [...allCommands].sort(() => Math.random() - 0.5)
  ).current;

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const timestamp = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      const prefix = FEED_PREFIXES[Math.floor(Math.random() * FEED_PREFIXES.length)];
      const status = FEED_STATUSES[Math.floor(Math.random() * FEED_STATUSES.length)];
      const cmd = commands[currentIdx % commands.length];

      setLines((prev) => {
        const newLines = [...prev, { timestamp, prefix, command: cmd, status }];
        return newLines.slice(-count);
      });
      setCurrentIdx((prev) => prev + 1);
    }, interval);

    return () => clearInterval(timer);
  }, [currentIdx, commands, count, interval]);

  return (
    <div className={`font-mono text-xs overflow-hidden ${className}`}>
      {lines.map((line, i) => (
        <div
          key={i}
          className="flex gap-2 py-0.5 animate-fadeIn"
          style={{ opacity: 0.4 + (i / lines.length) * 0.6 }}
        >
          <span className="text-cyber-muted flex-shrink-0">{line.timestamp}</span>
          <span className={`flex-shrink-0 ${
            line.prefix.includes('EXPLOIT') || line.prefix.includes('CRACK')
              ? 'text-cyber-red'
              : line.prefix.includes('SCAN') || line.prefix.includes('RECON')
              ? 'text-cyber-cyan'
              : 'text-cyber-purple'
          }`}>{line.prefix}</span>
          <span className="text-cyber-green truncate flex-1">{line.command}</span>
          <span className={`flex-shrink-0 ${
            line.status === 'WARN' ? 'text-cyber-amber' :
            line.status === 'CRIT' ? 'text-cyber-red' : 'text-cyber-green'
          }`}>[{line.status}]</span>
        </div>
      ))}
    </div>
  );
};

// Static command block for background decoration
export const CommandBlock = ({ commands, title, className = '' }) => (
  <div className={`font-mono text-xs leading-relaxed ${className}`}>
    {title && <div className="text-cyber-cyan mb-2">{'// '}{title}</div>}
    {commands.map((cmd, i) => (
      <div key={i} className="text-cyber-green/30 truncate">
        <span className="text-cyber-muted/30">$</span> {cmd}
      </div>
    ))}
  </div>
);

export default CommandStream;
